import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMoeda } from "../../../../core/hooks/useMoeda";
import { Cartao, Rotulo } from "../../../../core/styles/superficies";
import { IPagamentoPedido } from "../../../interface/IPagamentoPedido";

interface ResumoFinanceiroProps {
    valorTotal: number;
    pagamentos: IPagamentoPedido[];
    compacto: boolean;
}

/**
 * Os números que decidem o que fazer com o pedido, no topo e fora dos cartões de detalhe.
 *
 * O que aparece depende de como o estabelecimento trabalha. Marcar pagamento a pagamento é
 * opcional, e boa parte só registra a venda no fechamento — para esses, `pago` fica falso em
 * tudo e um "falta receber" calculado pelas flags acusaria dívida num pedido quitado. Por isso
 * o recebimento só é exibido quando há ao menos um pagamento confirmado, que é a evidência de
 * que aquele controle está em uso ali.
 */
export const ResumoFinanceiro = ({ valorTotal, pagamentos, compacto }: ResumoFinanceiroProps) => {
    const { real } = useMoeda();

    const controleEmUso = pagamentos.some(pagamento => pagamento.pago);

    const valorPago = pagamentos
        .filter(pagamento => pagamento.pago)
        .reduce((total, pagamento) => total + (pagamento.valor ?? 0), 0);

    // Arredonda antes de comparar: a soma dos pagamentos costuma vir com resíduo de centavo e
    // faria a tela acusar diferença onde o operador não vê nenhuma.
    const restante = Math.round((valorTotal - valorPago) * 100) / 100;
    const quitado = restante <= 0;

    const metodos = Array.from(
        new Set(pagamentos.map(pagamento => pagamento.metodoPagamento).filter(Boolean) as string[])
    );

    const itens = controleEmUso
        ? [
            { rotulo: "Total do pedido", valor: real(valorTotal), cor: "text.primary" },
            { rotulo: "Pago", valor: real(valorPago), cor: "success.main" },
            {
                rotulo: quitado ? "Situação" : "Falta receber",
                valor: quitado ? "Quitado" : real(restante),
                cor: quitado ? "success.main" : "warning.main",
            },
        ]
        : [
            { rotulo: "Total do pedido", valor: real(valorTotal), cor: "text.primary" },
            {
                rotulo: metodos.length > 1 ? "Formas de pagamento" : "Forma de pagamento",
                // Sem pagamento registrado o campo fica vazio em vez de sugerir um valor.
                valor: metodos.length ? metodos.join(" · ") : "Não informada",
                cor: metodos.length ? "text.primary" : "text.disabled",
            },
        ];

    return (
        <Cartao
            sx={{
                display: "grid",
                gridTemplateColumns: compacto ? "1fr" : `repeat(${itens.length}, 1fr)`,
                // Sem gap: as divisórias abaixo é que separam as colunas.
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.03),
            }}
        >
            {itens.map((item, indice) => (
                <Box
                    key={item.rotulo}
                    sx={{
                        padding: 2,
                        minWidth: 0,
                        borderLeft: theme => (!compacto && indice > 0 ? `1px solid ${theme.palette.divider}` : "none"),
                        borderTop: theme => (compacto && indice > 0 ? `1px solid ${theme.palette.divider}` : "none"),
                    }}
                >
                    <Rotulo sx={{ display: "block", mb: 0.5 }}>{item.rotulo}</Rotulo>
                    <Typography
                        sx={{
                            // A lista de métodos é texto e pode ser longa; os valores monetários
                            // continuam no corpo grande.
                            fontSize: item.rotulo.startsWith("Forma") ? "1rem" : "1.375rem",
                            fontWeight: item.rotulo.startsWith("Forma") ? 600 : 700,
                            lineHeight: 1.3,
                            color: item.cor,
                            wordBreak: "break-word",
                        }}
                    >
                        {item.valor}
                    </Typography>
                </Box>
            ))}
        </Cartao>
    );
};
